import { useEffect, type RefObject } from 'react';

const clamp = (n: number) => Math.min(1, Math.max(0, n));

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)
  );
}

/**
 * Runs `update` now, and again on scroll and resize, at most once per frame.
 * Returns the cleanup.
 */
export function subscribeToScroll(update: () => void): () => void {
  let frame = 0;
  const run = () => {
    frame = 0;
    update();
  };
  const onEvent = () => {
    if (!frame) frame = requestAnimationFrame(run);
  };
  window.addEventListener('scroll', onEvent, { passive: true });
  window.addEventListener('resize', onEvent);
  update();
  return () => {
    window.removeEventListener('scroll', onEvent);
    window.removeEventListener('resize', onEvent);
    if (frame) cancelAnimationFrame(frame);
  };
}

/**
 * Scroll-linked motion without React in the loop.
 *
 * The scroll position is written straight onto the element as a CSS variable
 * (`--hero-p`, 0 at the top of the page and 1 once the hero has scrolled away),
 * and the stylesheet turns that into `transform` and `opacity`. Nothing
 * re-renders per frame, and only compositor-friendly properties move, which is
 * what keeps it smooth on phones.
 *
 * Reduced motion leaves the variable at 0: everything stays at rest.
 */
export function useHeroProgress(ref: RefObject<HTMLElement | null>): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      el.style.setProperty('--hero-p', '0');
      return;
    }
    return subscribeToScroll(() => {
      // Measured against the hero's own height, so it is the same on any phone.
      // Negative while iOS rubber-bands past the top: clamped to 0.
      const range = Math.max(el.offsetHeight * 0.85, 1);
      el.style.setProperty('--hero-p', clamp(window.scrollY / range).toFixed(4));
    });
  }, [ref]);
}

/**
 * The reverse of the hero: `--reveal-p` runs from 0 to 1 as the element rises
 * into the screen, so it can grow into place as the reader arrives at it.
 *
 * 0 when its top edge is at `from` (a fraction of the screen height, 1 = the
 * bottom edge), and 1 when it has risen to `to`. Reduced motion shows it at
 * rest (1) straight away.
 */
export function useRevealProgress(
  ref: RefObject<HTMLElement | null>,
  { from = 1, to = 0.45 }: { from?: number; to?: number } = {},
): void {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      el.style.setProperty('--reveal-p', '1');
      return;
    }
    return subscribeToScroll(() => {
      const vh = window.innerHeight;
      const top = el.getBoundingClientRect().top;
      const p = (vh * from - top) / Math.max(vh * (from - to), 1);
      el.style.setProperty('--reveal-p', clamp(p).toFixed(4));
    });
  }, [ref, from, to]);
}
